import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { RiArrowRightSLine } from "react-icons/ri";
import { getRequest, putRequest } from "../../helpers/api.helper";
import { totalPropertyValueOptions } from "../../utils/constants";

const Step9Edit = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = location.state || {};

  // Add profile check
  if (!profile) {
    return (
      <div className="flex justify-center items-center h-screen text-red-600 font-semibold">
        ⚠️ Profile data not available.
      </div>
    );
  }

  /** ---------- State Management ---------- **/
  const [modalData, setModalData] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [city, setCity] = useState<any[]>([]);
  const [education, setEducation] = useState<any[]>([]);
  const [annualIncome, setAnnualIncome] = useState<any[]>([]);
  const [occupation, setOccupation] = useState<any[]>([]);
  const [country, setCountry] = useState<any[]>([]);
  const [state, setState] = useState<any[]>([]);
  const [caste, setCaste] = useState<any[]>([]);
  const [subcaste, setSubcaste] = useState<any[]>([]);
  const [star, setStar] = useState<any[]>([]);
  const [rashi, setRashi] = useState<any[]>([]);
  const [dosham, setDosham] = useState<any[]>([]);

  // Helper function to parse array or fallback to empty array
  const parsePreferences = (value: any) => {
    // Treat null/undefined/"" as Any
    if (value === null || value === undefined) return [];
    if (typeof value === "string") {
      const cleaned = value.trim();
      if (!cleaned) return [];
      // if stored as "Any" / "any"
      if (cleaned.toLowerCase() === "any") return [];

      // if stored as comma string
      const parts = cleaned
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean)
        .filter((v) => v.toLowerCase() !== "any");

      return parts;
    }

    if (Array.isArray(value)) {
      // if stored as ["any"] / ["Any"]
      const arr = value.map((v) => String(v).trim()).filter(Boolean);

      // if only any exists, treat as Any
      const nonAny = arr.filter((v) => v.toLowerCase() !== "any");
      return nonAny.length ? nonAny : [];
    }

    return [];
  };

  const getAllSelectableValues = (key: string) => {
    const opts = dropdownOptions[key]?.options || [];
    return opts.map((o: any) => o.value);
  };

  const isAnyState = (key: string) => {
    const v = selectedOptions[key];
    return v === undefined || (Array.isArray(v) && v.length === 0);
  };

  const generateHeightRange = (from?: string, to?: string) => {
    if (!from || !to) return [];

    const start = parseFloat(from);
    const end = parseFloat(to);

    const values: string[] = [];
    for (let val = start; val <= end + 0.001; val += 0.1) {
      values.push(val.toFixed(1));
    }

    return values;
  };

  const generateAgeRange = (from?: string, to?: string) => {
    if (!from || !to) return [];

    const start = parseInt(from);
    const end = parseInt(to);

    const values: string[] = [];
    for (let i = start; i <= end; i++) {
      values.push(String(i));
    }

    return values;
  };

  // Initialize with profile data - all partner preferences
  const [selectedOptions, setSelectedOptions] = useState<any>({
    // Block 1: Religion & Caste
    Religion_Preferences: parsePreferences(profile?.religionPreferences),
    Caste_Preferences: parsePreferences(profile?.castePreferences),
    Sub_Caste_Preferences: parsePreferences(profile?.subCastePreferences),
    Mother_Toungue: parsePreferences(profile?.motherTonguePreferences),
    Marital_Status_Preferences: parsePreferences(
      profile?.maritalStatusPreferences
    ),
    children_PreferenceOptions: parsePreferences(profile?.childrenPreferences),
    Age_Preferences: (() => {
      const values = parsePreferences(profile?.agePreferences);
      if (!values.length) return { from: "", to: "" };

      return {
        from: values[0],
        to: values[values.length - 1],
      };
    })(),
    Height_Preferences: (() => {
      const values = parsePreferences(profile?.heightPreferences);
      if (!values.length) return { from: "", to: "" };

      return {
        from: values[0],
        to: values[values.length - 1],
      };
    })(),
    Partner_Created_Preferences: parsePreferences(profile?.partnerCreatedBy),
    Partner_Service_Preferences: parsePreferences(
      profile?.partnerServicePreference
    ),

    // Block 2: Education & Job
    Education_Preferences: parsePreferences(
      profile?.partnerEducationPreferences
    ),
    Occupation_Preference: parsePreferences(
      profile?.partnerOccupationPreferences
    ),
    Job_Location_Preference: parsePreferences(
      profile?.partnerJobLocationPreferences
    ),
    Anuual_Income_Preference: parsePreferences(profile?.partnerAnnualIncome),
    Partner_Family_Preference: parsePreferences(profile?.familyPreferences),
    Setelled_Location_Preference: parsePreferences(
      profile?.settledLocationPreferences
    ),

    // Block 3: Property
    House_Type_Preference: parsePreferences(profile?.ownHousePreferences),
    Own_House_sq_Yard_Preference: parsePreferences(
      profile?.squareYardPreferences
    ),
    Monthly_Rent_Preference: parsePreferences(profile?.monthlyRentPreferences),
    Open_Plots_Preference: parsePreferences(profile?.plotPreference),
    Flats_Preference: parsePreferences(profile?.flatPreference),
    Own_Location_Preference: parsePreferences(profile?.ownLocationPreferences),
    Agriculture_Land_Preference: parsePreferences(
      profile?.agricultureLandPreference
    ),
    Total_Property_Value_Preference: parsePreferences(
      profile?.totalPropertyValuePreference
    ),

    // Block 4: Location
    Country_Preference: parsePreferences(profile?.countryLocationPreferences),
    State_Preference: parsePreferences(profile?.stateLocationPreferences),
    City_Preference: parsePreferences(profile?.cityLocationPreferences),
    Citizenship_Preference: parsePreferences(profile?.citizenshipPreferences),

    Star_Preference: parsePreferences(profile?.starPreferences),
    Rashi_Preference: parsePreferences(profile?.rashiPreferences),
    Dosham_Preference: parsePreferences(profile?.doshamPreferences),

    Eating_Preference: parsePreferences(profile?.eatingHabitsPreferences),
    Drinking_Preference: parsePreferences(profile?.drinkingHabitsPreferences),
    Smoking_Preference: parsePreferences(profile?.smokingHabitsPreferences),
  });

  /** ---------- Dropdown Options ---------- **/
  const dropdownOptions: any = {
    // Block 1: Religion & Caste Preferences
    Religion_Preferences: {
      section: "religion",
      type: "multi",
      options: [
        { value: "hindu", label: "Hindu" },
        { value: "muslim", label: "Muslim" },
        { value: "christian", label: "Christian" },
        { value: "sikh", label: "Sikh" },
        { value: "buddhist", label: "Buddhist" },
        { value: "jain", label: "Jain" },
        { value: "parsi", label: "Parsi" },
        { value: "other", label: "Other" },
      ],
    },
    Caste_Preferences: {
      section: "religion",
      type: "multi",
      options: caste.map((c) => ({ value: c.caste, label: c.caste })),
    },
    Sub_Caste_Preferences: {
      section: "religion",
      type: "multi",
      options: subcaste.map((sc) => ({
        value: sc.sub_caste,
        label: sc.sub_caste,
      })),
    },
    Mother_Toungue: {
      section: "religion",
      type: "multi",
      options: [
        { value: "bengali", label: "Bengali" },
        { value: "english", label: "English" },
        { value: "hindi", label: "Hindi" },
        { value: "kannada", label: "Kannada" },
        { value: "marathi", label: "Marathi" },
        { value: "tamil", label: "Tamil" },
        { value: "telugu", label: "Telugu" },
      ],
    },
    Marital_Status_Preferences: {
      section: "religion",
      type: "multi",
      options: [
        { value: "neverMarried", label: "Never Married" },
        { value: "awaitingDivorce", label: "Awaiting Divorce" },
        { value: "divorced", label: "Divorced" },
        { value: "widow", label: "Widowed" },
      ],
    },
    children_PreferenceOptions: {
      section: "religion",
      type: "multi",
      options: [
        { value: "no_children", label: "No children" },
        { value: "want_children", label: "Want children" },
        { value: "has_children", label: "Has children" },
        { value: "not_sure_yet", label: "Not sure yet" },
      ],
    },
    Age_Preferences: {
      section: "religion",
      type: "range", // optional (just for clarity)
      options: Array.from({ length: 83 }, (_, i) => {
        const age = 18 + i; // 18–100
        return {
          value: String(age),
          label: `${age} years`,
        };
      }),
    },
    Height_Preferences: {
      section: "religion",
      type: "multi",
      options: Array.from({ length: 26 }, (_, i) => ({
        value: (4 + i * 0.1).toFixed(1),
        label: `${(4 + i * 0.1).toFixed(1)} ft`,
      })),
    },
    Partner_Created_Preferences: {
      section: "religion",
      type: "multi",
      options: [
        { value: "self", label: "Self" },
        { value: "father", label: "Father" },
        { value: "mother", label: "Mother" },
        { value: "friend", label: "Friend" },
        { value: "relatives", label: "Relatives" },
        { value: "sister", label: "Sister" },
        { value: "brother", label: "Brother" },
      ],
    },
    Partner_Service_Preferences: {
      section: "religion",
      type: "multi",
      options: [
        { value: "only_online", label: "Only Online Service" },
        { value: "only_offline", label: "Only Offline Service" },
        { value: "online_offline", label: "Online/Offline Service" },
      ],
    },

    // Block 2: Education & Job Preferences
    Education_Preferences: {
      section: "education",
      type: "multi",
      options: education.map((edu) => ({
        value: edu.education,
        label: edu.education,
      })),
    },
    Occupation_Preference: {
      section: "education",
      type: "multi",
      options: occupation.map((occ) => ({
        value: occ.occupation,
        label: occ.occupation,
      })),
    },
    Job_Location_Preference: {
      section: "education",
      type: "multi",
      options: city.map((c) => ({ value: c.city, label: c.city })),
    },
    Anuual_Income_Preference: {
      section: "education",
      type: "multi",
      options: annualIncome.map((income) => ({
        value: income.annual_income,
        label: income.annual_income,
      })),
    },
    Partner_Family_Preference: {
      section: "education",
      type: "multi",
      options: [
        { value: "Joint_Family", label: "Joint Family" },
        { value: "Nuclear_Family", label: "Nuclear Family" },
        { value: "Family-Oriented_Partner", label: "Family-Oriented Partner" },
        { value: "Independent_Partner", label: "Independent Partner" },
        { value: "Living_with_Parents", label: "Living with Parents" },
      ],
    },
    Setelled_Location_Preference: {
      section: "education",
      type: "multi",
      options: city.map((c) => ({ value: c.city, label: c.city })),
    },

    // Block 3: Property Preferences
    House_Type_Preference: {
      section: "property",
      type: "multi",
      options: [
        { value: "GroundFloor", label: "Ground Floor" },
        { value: "g+1", label: "G+1" },
        { value: "g+2", label: "G+2" },
        { value: "g+3", label: "G+3" },
        { value: "g+4", label: "G+4" },
        { value: "g+5", label: "G+5" },
        { value: "g+6", label: "G+6" },
        { value: "g+7", label: "G+7" },
        { value: "g+8", label: "G+8" },
        { value: "g+9", label: "G+9" },
        { value: "g+10", label: "G+10" },
      ],
    },
    Own_House_sq_Yard_Preference: {
      section: "property",
      type: "multi",
      options: Array.from(
        { length: 1000 - 70 + 1 },
        (_, index) => 70 + index
      ).map((sqYards) => ({
        value: String(sqYards),
        label: `${sqYards} Yards`,
      })),
    },
    Monthly_Rent_Preference: {
      section: "property",
      type: "multi",
      options: Array.from(
        { length: 200 },
        (_, index) => 50000 + index * 50000
      ).map((value) => ({
        value: String(value),
        label: `₹${value.toLocaleString("en-IN")}`,
      })),
    },

    Open_Plots_Preference: {
      section: "property",
      type: "multi",
      options: Array.from({ length: 20 }, (_, i) => i + 1).map((value) => ({
        value: String(value),
        label: value.toString(),
      })),
    },

    Flats_Preference: {
      section: "property",
      type: "multi",
      options: Array.from({ length: 20 }, (_, i) => i + 1).map((value) => ({
        value: String(value),
        label: value.toString(),
      })),
    },

    Own_Location_Preference: {
      section: "property",
      type: "multi",
      options: city.map((c) => ({ value: c.city, label: c.city })),
    },
    Agriculture_Land_Preference: {
      section: "property",
      type: "multi",
      options: Array.from(
        { length: 1000 - 70 + 1 },
        (_, index) => 70 + index
      ).map((acres) => ({
        value: String(acres),
        label: `${acres} Acres`,
      })),
    },

    Total_Property_Value_Preference: {
      section: "property",
      type: "multi",
      options: totalPropertyValueOptions,
    },

    // Block 4: Location Preferences
    Country_Preference: {
      section: "location",
      type: "multi",
      options: country.map((c) => ({ value: c.country, label: c.country })),
    },
    State_Preference: {
      section: "location",
      type: "multi",
      options: state.map((s) => ({ value: s.state, label: s.state })),
    },
    City_Preference: {
      section: "location",
      type: "multi",
      options: city.map((c) => ({ value: c.city, label: c.city })),
    },
    Citizenship_Preference: {
      section: "location",
      type: "multi",
      options: [
        { value: "Indian", label: "Indian" },
        { value: "NRI", label: "NRI" },
      ],
    },

    Star_Preference: {
      section: "horoscope",
      type: "multi",
      options: star.map((s) => ({ value: s.star, label: s.star })),
    },
    Rashi_Preference: {
      section: "horoscope",
      type: "multi",
      options: rashi.map((r) => ({
        value: r.raasi ?? r.rashi,
        label: r.raasi ?? r.rashi,
      })),
    },
    Dosham_Preference: {
      section: "horoscope",
      type: "multi",
      options: dosham.map((d) => ({ value: d.dosham, label: d.dosham })),
    },

    Eating_Preference: {
      section: "habits",
      type: "multi",
      options: [
        { value: "nonVegetarian", label: "Non Vegetarian" },
        { value: "vegetarian", label: "Vegetarian" },
        { value: "egg", label: "Eggeterian" },
      ],
    },
    Drinking_Preference: {
      section: "habits",
      type: "multi",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
        { value: "occasionally", label: "Occasional" },
      ],
    },
    Smoking_Preference: {
      section: "habits",
      type: "multi",
      options: [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
        { value: "occasionally", label: "Occasional" },
      ],
    },
  };

  /** ---------- Fetch Dropdown Data ---------- **/
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRequest("/common/profile/step9");
        if (response.success) {
          // Helper function to ensure unique items (by key or entire value)
          const uniqueByKey = (arr: any, key: any) => {
            if (!Array.isArray(arr)) return [];
            const seen = new Set();
            return arr.filter((item) => {
              const val = key ? item[key] : item;
              if (seen.has(val)) return false;
              seen.add(val);
              return true;
            });
          };

          setCity(uniqueByKey(response.data.city, "city") || []);
          setEducation(uniqueByKey(response.data.education, "education") || []);
          setAnnualIncome(
            uniqueByKey(response.data.annual_income, "annual_income") || []
          );
          setOccupation(
            uniqueByKey(response.data.occupation, "occupation") || []
          );
          setCountry(uniqueByKey(response.data.country, "country") || []);
          setState(uniqueByKey(response.data.state, "state") || []);
          setCaste(uniqueByKey(response.data.caste, "caste") || []);
          setSubcaste(uniqueByKey(response.data.sub_caste, "sub_caste") || []);

          setStar(uniqueByKey(response.data.star, "star") || []);
          setRashi(uniqueByKey(response.data.raasi, "raasi") || []);
          setDosham(uniqueByKey(response.data.dosham, "dosham") || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  /** ---------- Effects ---------- **/
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (modalData) {
      setSearchTerm("");
    }
  }, [modalData]);

  /** ---------- Handlers ---------- **/
  const openModal = (key: string) => {
    const cfg = dropdownOptions[key];
    if (!cfg) return; // safety
    setModalData({ key, type: cfg.type, options: cfg.options || [] });
  };

  const closeModal = () => {
    setModalData(null);
    setSearchTerm("");
  };

  const handleMultiSelectToggle = (value: any) => {
    if (!modalData?.key) return;

    const key = modalData.key;
    const allValues = getAllSelectableValues(key);

    const raw = selectedOptions[key];
    const base =
      raw === undefined
        ? allValues // default Any
        : Array.isArray(raw) && raw.length === 0
        ? allValues // Any => treat as all selected
        : Array.isArray(raw)
        ? raw
        : [];

    const updated = base.includes(value)
      ? base.filter((v: any) => v !== value)
      : [...base, value];

    const isAll =
      allValues.length > 0 && allValues.every((v: any) => updated.includes(v));

    setSelectedOptions((prev: any) => ({
      ...prev,
      [key]: isAll ? [] : updated, // ✅ store [] when all selected
    }));
  };

  const normalizePreferenceArray = (arr: any[], options?: any[]) => {
    if (arr === null || !Array.isArray(arr) || arr.length === 0) return ["any"];

    const unique = Array.from(new Set(arr.map(String)));

    if (options && unique.length === options.length) return ["any"];

    return unique;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    const height = selectedOptions["Height_Preferences"];
    if (
      height?.from &&
      height?.to &&
      parseFloat(height.from) > parseFloat(height.to)
    ) {
      alert("From height cannot be greater than To height");
      return;
    }
    const age = selectedOptions["Age_Preferences"];
    if (age?.from && age?.to && parseInt(age.from) > parseInt(age.to)) {
      alert("From age cannot be greater than To age");
      return;
    }

    const normalize = (key: string) =>
      normalizePreferenceArray(
        selectedOptions[key],
        dropdownOptions[key]?.options
      );

    const formDataToSend = {
      partnerServicePreference: normalize("Partner_Service_Preferences"),
      partnerCreatedBy: normalize("Partner_Created_Preferences"),

      religionPreferences: normalize("Religion_Preferences"),
      castePreferences: normalize("Caste_Preferences"),
      subCastePreferences: normalize("Sub_Caste_Preferences"),
      maritalStatusPreferences: normalize("Marital_Status_Preferences"),
      childrenPreferences: normalize("children_PreferenceOptions"),
      motherTonguePreferences: normalize("Mother_Toungue"),
      agePreferences: generateAgeRange(
        selectedOptions["Age_Preferences"]?.from,
        selectedOptions["Age_Preferences"]?.to
      ),
      heightPreferences: generateHeightRange(
        selectedOptions["Height_Preferences"]?.from,
        selectedOptions["Height_Preferences"]?.to
      ),

      partnerEducationPreferences: normalize("Education_Preferences"),
      partnerOccupationPreferences: normalize("Occupation_Preference"),
      partnerJobLocationPreferences: normalize("Job_Location_Preference"),
      partnerAnnualIncome: normalize("Anuual_Income_Preference"),
      familyPreferences: normalize("Partner_Family_Preference"),
      settledLocationPreferences: normalize("Setelled_Location_Preference"),

      ownHousePreferences: normalize("House_Type_Preference"),
      squareYardPreferences: normalize("Own_House_sq_Yard_Preference"),
      monthlyRentPreferences: normalize("Monthly_Rent_Preference"),
      plotPreference: normalize("Open_Plots_Preference"),
      flatPreference: normalize("Flats_Preference"),
      ownLocationPreferences: normalize("Own_Location_Preference"),
      agricultureLandPreference: normalize("Agriculture_Land_Preference"),
      totalPropertyValuePreference: normalize(
        "Total_Property_Value_Preference"
      ),

      countryLocationPreferences: normalize("Country_Preference"),
      stateLocationPreferences: normalize("State_Preference"),
      cityLocationPreferences: normalize("City_Preference"),
      citizenshipPreferences: normalize("Citizenship_Preference"),

      starPreferences: normalize("Star_Preference"),
      rashiPreferences: normalize("Rashi_Preference"),
      doshamPreferences: normalize("Dosham_Preference"),

      eatingHabitsPreferences: normalize("Eating_Preference"),
      drinkingHabitsPreferences: normalize("Drinking_Preference"),
      smokingHabitsPreferences: normalize("Smoking_Preference"),

      step9: 1,
    };
    try {
      const response = await putRequest(
        `/bureau/profile/${profile._id}`,
        formDataToSend
      );
      if (response.success) {
        alert("Partner Preferences updated successfully!");
        navigate(-1);
      } else {
        alert("Failed to update details.");
      }
    } catch (error) {
      console.error("Error updating details:", error);
      alert("An error occurred while updating details.");
    }
  };

  const handleBackClick = () => {
    navigate(-1);
  };

  /** ---------- Helper Functions ---------- **/
  const getDisplayValue = (key: any) => {
    if (key === "Height_Preferences") {
      const from = selectedOptions[key]?.from;
      const to = selectedOptions[key]?.to;

      if (!from && !to) return "Any";
      if (from && to) return `${from} ft - ${to} ft`;
      if (from) return `From ${from} ft`;
      if (to) return `Up to ${to} ft`;
    }

    if (key === "Age_Preferences") {
      const from = selectedOptions[key]?.from;
      const to = selectedOptions[key]?.to;

      if (!from && !to) return "Any";
      if (from && to) return `${from} - ${to} years`;
      if (from) return `From ${from} years`;
      if (to) return `Up to ${to} years`;
    }
    const value = selectedOptions[key] || [];
    const options = dropdownOptions[key]?.options || [];

    if (!value || value.length === 0) return "Any";
    if (options.length > 0 && value.length === options.length) return "Any";

    const labels = options
      .filter((opt: any) => value.includes(opt.value))
      .map((opt: any) => opt.label);

    // ✅ If options not loaded yet OR mismatch, show raw values
    if (labels.length === 0) return value.join(", ");

    return labels.join(", ");
  };

  // Group fields by section
  const getFieldsBySection = (section: any) => {
    return Object.keys(dropdownOptions).filter(
      (key) => dropdownOptions[key].section === section
    );
  };

  /** ---------- Reusable Modal Component ---------- **/
  const Modal = () => {
    if (!modalData) return null;

    const filteredOptions = modalData.options.filter((option: any) =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const allValues = modalData.options.map((o: any) => o.value);

    const raw = selectedOptions[modalData.key];
    const anyChecked =
      raw === undefined || (Array.isArray(raw) && raw.length === 0);

    // ✅ UI checked values
    const uiSelected = anyChecked ? allValues : Array.isArray(raw) ? raw : [];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
        <div className="bg-white p-6 rounded-lg max-w-md w-full shadow-lg m-5">
          <h2 className="text-lg font-bold mb-4 capitalize text-center">
            Select {modalData.key.replace(/_/g, " ")}
          </h2>

          {/* Select All Checkbox */}
          <div className="flex items-center space-x-2 mb-4">
            <input
              type="checkbox"
              checked={anyChecked}
              onChange={(e) => {
                const checked = e.target.checked;

                setSelectedOptions((prev: any) => ({
                  ...prev,
                  [modalData.key]: checked ? [] : null, // ✅ checked => Any, unchecked => none selected
                }));
              }}
              className="form-checkbox h-4 w-4 text-red-600"
            />

            <label className="block text-gray-700 font-medium">Any</label>
          </div>

          {/* Search Bar */}
          <input
            type="text"
            autoFocus
            className="w-full px-4 py-2 mb-4 border border-gray-300 rounded focus:outline-none focus:ring focus:ring-blue-200"
            placeholder={`Search ${modalData.key.replace(/_/g, " ")}`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />

          {/* Options List */}
          <div className="h-64 overflow-y-auto border-t border-b border-gray-200">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option: any, index: any) => (
                <React.Fragment key={option.value}>
                  <div className="flex items-center space-x-2 py-2">
                    <input
                      type="checkbox"
                      checked={uiSelected.includes(option.value)}
                      onChange={() => handleMultiSelectToggle(option.value)}
                      className="form-checkbox h-4 w-4 text-red-600"
                    />
                    <label
                      className="block text-gray-700 cursor-pointer"
                      onClick={() => handleMultiSelectToggle(option.value)}
                    >
                      {option.label}
                    </label>
                  </div>
                  {index < filteredOptions.length - 1 && (
                    <hr className="border-gray-200" />
                  )}
                </React.Fragment>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No options found
              </div>
            )}
          </div>

          {/* Close Button */}
          <button
            className="mt-4 w-full px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
            onClick={closeModal}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  /** ---------- Render ---------- **/
  return (
    <div className="profile-edit-page flex justify-center items-center min-h-screen bg-white">
      <div className="w-full max-w-lg bg-white shadow-lg rounded-lg">
        {/* Header */}
        <div className="w-full z-50 bg-red-600 fixed top-0 mb-12 p-2 px-3 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <button className="flex items-center" onClick={handleBackClick}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-center text-white py-3">
              Step 9 -
            </h1>
            <h5 className="font-semibold text-white">Partner Preferences</h5>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6 px-3 mt-24 pb-6">
          {/* Block 1: Caste & Religion Preferences */}
          <section className="shadow-2xl bg-white px-2 py-4 rounded">
            <div className="text-center pb-3">
              <div className="w-full bg-red-400 text-white p-3 rounded-lg font-semibold">
                Caste & Religion Preferences
              </div>
            </div>
            {getFieldsBySection("religion").map((key) => (
              <div key={key} className="mb-5">
                <label className="block text-red-500 font-medium mb-2 capitalize">
                  {key.replace(/_/g, " ")}
                </label>
                <div
                  className="flex flex-col w-full rounded-lg border px-3 border-gray-300 py-3 cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => {
                    if (
                      key === "Height_Preferences" ||
                      key === "Age_Preferences"
                    )
                      return;
                    openModal(key);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-md font-medium">
                      {getDisplayValue(key)}
                    </span>
                    <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                  </div>
                  {key === "Height_Preferences" && (
                    <div className="flex gap-2 mt-2">
                      <select
                        value={selectedOptions?.Height_Preferences?.from || ""}
                        onChange={(e) =>
                          setSelectedOptions((prev: any) => ({
                            ...prev,
                            Height_Preferences: {
                              ...prev.Height_Preferences,
                              from: e.target.value,
                            },
                          }))
                        }
                        className="w-full border rounded px-2 py-1 text-sm"
                      >
                        <option value="">From</option>
                        {dropdownOptions.Height_Preferences.options.map(
                          (opt: any) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          )
                        )}
                      </select>

                      <select
                        value={selectedOptions?.Height_Preferences?.to || ""}
                        onChange={(e) =>
                          setSelectedOptions((prev: any) => ({
                            ...prev,
                            Height_Preferences: {
                              ...prev.Height_Preferences,
                              to: e.target.value,
                            },
                          }))
                        }
                        className="w-full border rounded px-2 py-1 text-sm"
                      >
                        <option value="">To</option>
                        {dropdownOptions.Height_Preferences.options.map(
                          (opt: any) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  )}
                  {key === "Age_Preferences" && (
                    <div className="flex gap-2 mt-2">
                      <select
                        value={selectedOptions?.Age_Preferences?.from || ""}
                        onChange={(e) =>
                          setSelectedOptions((prev: any) => ({
                            ...prev,
                            Age_Preferences: {
                              ...prev.Age_Preferences,
                              from: e.target.value,
                            },
                          }))
                        }
                        className="w-full border rounded px-2 py-1 text-sm"
                      >
                        <option value="">From</option>
                        {dropdownOptions.Age_Preferences.options.map(
                          (opt: any) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          )
                        )}
                      </select>

                      <select
                        value={selectedOptions?.Age_Preferences?.to || ""}
                        onChange={(e) =>
                          setSelectedOptions((prev: any) => ({
                            ...prev,
                            Age_Preferences: {
                              ...prev.Age_Preferences,
                              to: e.target.value,
                            },
                          }))
                        }
                        className="w-full border rounded px-2 py-1 text-sm"
                      >
                        <option value="">To</option>
                        {dropdownOptions.Age_Preferences.options.map(
                          (opt: any) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </section>

          {/* Block 2: Education & Job Preferences */}
          <section className="shadow-2xl bg-white px-2 py-4 rounded">
            <div className="text-center pb-3">
              <div className="w-full bg-red-400 text-white p-3 rounded-lg font-semibold">
                Education & Job Preferences
              </div>
            </div>
            {getFieldsBySection("education").map((key) => (
              <div key={key} className="mb-5">
                <label className="block text-red-500 font-medium mb-2 capitalize">
                  {key.replace(/_/g, " ")}
                </label>
                <div
                  className="flex flex-col w-full rounded-lg border px-3 border-gray-300 py-3 cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => openModal(key)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-md font-medium">
                      {getDisplayValue(key)}
                    </span>
                    <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Block 3: Property Preferences */}
          <section className="shadow-2xl bg-white px-2 py-4 rounded">
            <div className="text-center pb-3">
              <div className="w-full bg-red-400 text-white p-3 rounded-lg font-semibold">
                Property Preferences
              </div>
            </div>
            {getFieldsBySection("property").map((key) => (
              <div key={key} className="mb-5">
                <label className="block text-red-500 font-medium mb-2 capitalize">
                  {key.replace(/_/g, " ")}
                </label>
                <div
                  className="flex flex-col w-full rounded-lg border px-3 border-gray-300 py-3 cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => openModal(key)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-md font-medium">
                      {getDisplayValue(key)}
                    </span>
                    <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Block 4: Location Preferences */}
          <section className="shadow-2xl bg-white px-2 py-4 rounded">
            <div className="text-center pb-3">
              <div className="w-full bg-red-400 text-white p-3 rounded-lg font-semibold">
                Location Preferences
              </div>
            </div>
            {getFieldsBySection("location").map((key) => (
              <div key={key} className="mb-5">
                <label className="block text-red-500 font-medium mb-2 capitalize">
                  {key.replace(/_/g, " ")}
                </label>
                <div
                  className="flex flex-col w-full rounded-lg border px-3 border-gray-300 py-3 cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => openModal(key)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-md font-medium">
                      {getDisplayValue(key)}
                    </span>
                    <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Block 5: Horoscope Preferences */}
          <section className="shadow-2xl bg-white px-2 py-4 rounded">
            <div className="text-center pb-3">
              <div className="w-full bg-red-400 text-white p-3 rounded-lg font-semibold">
                Horoscope Preferences
              </div>
            </div>
            {getFieldsBySection("horoscope").map((key) => (
              <div key={key} className="mb-5">
                <label className="block text-red-500 font-medium mb-2 capitalize">
                  {key.replace(/_/g, " ")}
                </label>
                <div
                  className="flex flex-col w-full rounded-lg border px-3 border-gray-300 py-3 cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => openModal(key)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-md font-medium">
                      {getDisplayValue(key)}
                    </span>
                    <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Block 6: Habits Preferences */}
          <section className="shadow-2xl bg-white px-2 py-4 rounded">
            <div className="text-center pb-3">
              <div className="w-full bg-red-400 text-white p-3 rounded-lg font-semibold">
                Habits Preferences
              </div>
            </div>
            {getFieldsBySection("habits").map((key) => (
              <div key={key} className="mb-5">
                <label className="block text-red-500 font-medium mb-2 capitalize">
                  {key.replace(/_/g, " ")}
                </label>
                <div
                  className="flex flex-col w-full rounded-lg border px-3 border-gray-300 py-3 cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => openModal(key)}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-md font-medium">
                      {getDisplayValue(key)}
                    </span>
                    <RiArrowRightSLine className="text-3xl text-gray-500 ml-3" />
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              className="w-full bg-red-600 text-white p-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
            >
              Update Partner Preferences
            </button>
          </div>
        </form>

        {/* Reusable Modal */}
        <Modal />
      </div>
    </div>
  );
};

export default Step9Edit;
